import {
  Box,
  BoxProps,
  Container,
  ContainerProps,
  HStack,
  Heading,
  ResponsiveValue,
} from "@chakra-ui/react";
import { ReactNode, useLayoutEffect, useRef, useState } from "react";
import Breadcrumbs from "../../components/Breadcrumb";
import ErrorSection from "../../components/ErrorSection";
import LoadingSection from "../../components/LoadingSection";
import { ScrollRestoration } from "react-router-dom";

type Props = {
  children: ReactNode;
  title?: string;
  description?: string;
  isLoading?: boolean;
  hasError?: boolean;
  hasBoxWrapper?: boolean;
  retry?: () => void;
  withBreadcrumb?: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  maxWidth?: ResponsiveValue<number | (string & {})>;
  bottomLeftContent?: JSX.Element;
  bottomRightContent?: JSX.Element;
  bottomLeftContainerProps?: BoxProps;
  bottomRightContainerProps?: BoxProps;
  containerProps?: ContainerProps;
};

export default function BasePage({
  title,
  description,
  children,
  isLoading,
  hasError,
  retry,
  hasBoxWrapper = true,
  withBreadcrumb = true,
  maxWidth = "1080px",
  containerProps,
  bottomLeftContent,
  bottomRightContent,
  bottomLeftContainerProps,
  bottomRightContainerProps,
}: Props) {
  const bottomStackRef = useRef<HTMLDivElement>(null);
  const [paddingBottom, setPaddingBottom] = useState(0);

  useLayoutEffect(() => {
    setTimeout(() => {
      if (bottomStackRef.current) {
        setPaddingBottom(bottomStackRef.current.offsetHeight);
      }
    }, 250);
  }, []);

  return (
    <Container minH="100vh" maxW={maxWidth} flexGrow={1} {...containerProps}>
      {title && (
        <Box mt={8} gap={10}>
          {title && (
            <Heading as="h1" size="lg" fontWeight="500">
              {title}
            </Heading>
          )}
          {description && (
            <Heading as="h2" mt={4} size="sm" fontWeight="400">
              {description}
            </Heading>
          )}
        </Box>
      )}
      <Box mt={4} mb={8}>
        {withBreadcrumb && <Breadcrumbs />}
      </Box>
      {isLoading ? (
        <LoadingSection />
      ) : hasError ? (
        <ErrorSection retry={retry} />
      ) : hasBoxWrapper ? (
        <Box style={{ paddingBottom }}>{children}</Box>
      ) : (
        children
      )}
      {!isLoading && !hasError && (
        <HStack
          ref={bottomStackRef}
          justifyContent="space-between"
          alignItems="center"
          position="fixed"
          bottom={0}
          left={0}
          right={0}
          borderColor="gray.100"
          bg="gray.50"
          borderTopWidth={1}
        >
          <Box {...bottomLeftContainerProps}>{bottomLeftContent}</Box>
          <Box {...bottomRightContainerProps}>{bottomRightContent}</Box>
        </HStack>
      )}
      <ScrollRestoration />
    </Container>
  );
}
